import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    useIntl,
    usePagination,
    useToggle,
    useFileDownload,
    useFileDelete,
    useConfirm,
    useUpdateEffect,
} from 'hooks'
import { generateQuery } from 'utils/filter'
import Tags from 'features/tags'
import Share from 'features/share/Share'
import AddToCollection from 'features/add-files-collection'
import SearchAndFilter from 'features/patient-searchfilter/SearchAndFilter'
import PatientsListFallback from 'components/Fallback/ListBar'
import PatientsListItem from 'components/Patient/PatientListItem'
import { SidebarLoader, ListItemLoader } from 'components/Loaders'
import { ScrollableDirective } from 'components/Zui'
import {
    patientSelector,
    setPatient,
    resetFilter,
    getPatientsWithSummary,
    setFilterParams,
    setPage,
} from './patientSlice'
import './index.css'

const List = ({ selectedPatient }) => {
    const dispatch = useDispatch()
    const { t } = useIntl()
    const { getConfirmation } = useConfirm()
    const [addCollectionData, setAddCollectionData] = useState({})
    const [shareablePatient, setShareablePatient] = useState()
    const [hasMore, setHasMore] = useState(false)
    const [tagType, setTagType] = useState({})

    const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useToggle()
    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: onTagClose } = useToggle()
    const {
        isOpen: isCollectionOpen,
        onOpen: onCollectionOpen,
        onClose: onCollectionClose,
    } = useToggle()
    const patientsCount = useSelector(state => patientSelector.selectTotal(state))
    const patients = useSelector(state => patientSelector.selectAll(state))

    const {
        isLoading,
        isError,
        isSearched,
        isFiltered,
        filterParams,
        recordsCount,
        page,
        pageSize,
    } = useSelector(state => state.patients)

    const { download } = useFileDownload('patient')
    const { deleteFiles } = useFileDelete('patient')

    const totalPages = Math.ceil(recordsCount / pageSize)
    const currentPage = Math.ceil(patientsCount / pageSize)

    const CONTEXT_MENU_ITEMS = [
        {
            title: t('share'),
            icon: 'share',
            id: 'share',
        },
        {
            title: t('add.to.collection'),
            icon: 'movetofolderDark',
            id: 'move',
        },
        {
            title: 'Add/Edit Tags',
            icon: 'tagIcnDark',
            id: 'tag',
        },
        {
            title: t('download'),
            icon: 'downloadIcnDark',
            id: 'download',
        },
        {
            title: t('delete'),
            icon: 'delete',
            id: 'delete',
        },
    ]

    useEffect(() => {
        if (page < totalPages) {
            setHasMore(true)
        }
    }, [recordsCount])

    const handleGetPatients = () => {
        if (!isLoading) {
            dispatch(getPatientsWithSummary())
        }
    }

    useUpdateEffect(() => {
        if (recordsCount && page > currentPage && page <= totalPages) {
            handleGetPatients()
        }
        if (page < totalPages) {
            setHasMore(true)
        } else setHasMore(false)
    }, [page])

    useUpdateEffect(() => {
        if (page !== 1) dispatch(setPage(1))
        handleGetPatients()
    }, [filterParams])

    const lastElementRef = usePagination({
        isLoading,
        hasMore,
        action: () => dispatch(setPage(page + 1)),
    })

    const handleSelectPatient = useCallback(
        patient => {
            dispatch(setPatient(patient))
        },
        [dispatch]
    )

    const handleSharePatient = patient => {
        setShareablePatient({
            type: 'patient',
            id: patient.id,
            query: generateQuery(filterParams),
            name: patient.name,
        })
        onShareOpen()
    }

    const handleDownload = id => {
        download({ id })
    }

    const handleDelete = async id => {
        const confirmed = await getConfirmation({
            title: t('patient.confirm.delete.title'),
            message: t('patient.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })

        if (!confirmed) return

        deleteFiles({
            id,
        })
    }

    const handleResetFilters = () => {
        dispatch(resetFilter())
        dispatch(
            setFilterParams({
                type: 'filter',
                value: [],
            })
        )
    }

    const hasNoPatients = () => {
        return !isLoading && !patientsCount && !isError
    }

    const isInitLoading = () => {
        return isLoading && !patientsCount
    }

    const handleAddTag = id => {
        setTagType({
            type: 'patient',
            id,
        })
        onTagOpen()
    }

    const handleAddPatientFiles = id => {
        setAddCollectionData({
            type: 'patient',
            id,
        })
        onCollectionOpen()
    }

    return (
        <div className="patients-tab-wrapper">
            <SearchAndFilter />
            <div className="collection-count-clear-filter-wrapper">
                <div className="patients-count">
                    {recordsCount} {recordsCount > 1 ? t('patients') : t('patient')}
                </div>
                {isFiltered && isSearched && (
                    <div className="clear-applied-filter-wrapper">
                        <div className="filter-text">{t('results.hidden.filter')}.</div>
                        <div className="reset-filter">
                            <span className="reset-filter-text" onClick={handleResetFilters}>
                                {t('reset.filters')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
            <div className="patient-list-wrapper">
                {isInitLoading() && <SidebarLoader />}
                {hasNoPatients() && <PatientsListFallback title={t('fallback.no.patients')} />}
                {isError && <PatientsListFallback title={t('fallback.error.patients')} />}
                {recordsCount > 0 && (
                    <ScrollableDirective
                        style={{
                            maxHeight: '100%',
                            width: '100%',
                            flexGrow: '1',
                            overflow: 'auto',
                        }}
                    >
                        <>
                            {patients.map((patient, i) => {
                                const props = {
                                    key: patient.id,
                                    patient: patient,
                                    onClick: handleSelectPatient,
                                    selectedPatient: selectedPatient,
                                    handleSharePatient: handleSharePatient,
                                    contextMenu: CONTEXT_MENU_ITEMS,
                                    handleAddTag: handleAddTag,
                                    handleDownload: handleDownload,
                                    handleDelete: handleDelete,
                                    handleAddPatientFiles: handleAddPatientFiles,
                                }
                                return (
                                    <PatientsListItem
                                        ref={patients.length === i + 1 ? lastElementRef : null}
                                        {...props}
                                    />
                                )
                            })}
                            {isLoading && <ListItemLoader />}
                        </>
                    </ScrollableDirective>
                )}
            </div>
            {isCollectionOpen && (
                <AddToCollection onClose={onCollectionClose} data={addCollectionData} />
            )}
            {isTagOpen && <Tags onClose={onTagClose} data={tagType} />}
            {isShareOpen && <Share onClose={onShareClose} data={shareablePatient} />}
        </div>
    )
}

export default List
